import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Images, X } from 'lucide-react';
import type { FlowModel, FlowTextAnswers, Style, Ratio, Tone, Toolkit } from '../../../types';
import { uploadImageFull } from '../../../services/api';
import { RunningLoader } from '@/components/RunningLoader';

export interface EditDraft {
    flowModelId: string | undefined;
    answers: FlowTextAnswers;
    additionalRequirements: string;
    /** Per-question image fileIds (after pre-upload) */
    imageFiles: Record<string, string[]>;
}

interface SettingsCardProps {
    isEditMode: boolean;
    editDraft: EditDraft;
    setEditDraft: React.Dispatch<React.SetStateAction<EditDraft>>;
    flowModel: FlowModel;
    allFlowModels?: FlowModel[];
    styles: Style[];
    ratios: Ratio[];
    tones: Tone[];
    toolkits: Toolkit[];
    onSaveDraft?: (draft: EditDraft) => void;
    onCancelEdit?: () => void;
    onUseImageAsInput?: () => void;
    compact?: boolean;
}

const rowClass = (compact?: boolean) =>
    `flex justify-between items-center border-b border-slate-100 dark:border-slate-800 ${compact ? 'pb-1.5' : 'pb-2'}`;

const selectClass =
    'bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 text-xs text-slate-700 dark:text-slate-200 focus:outline-none';

type SelectOption = { value: string; label: string };

function resolveSelectOptions(
    qId: string,
    qOptions: { value: string; label?: string }[] | undefined,
    ratios: Ratio[],
    styles: Style[],
    tones: Tone[],
): SelectOption[] {
    if (qOptions && qOptions.length > 0) return qOptions.map(o => ({ value: o.value, label: o.label ?? o.value }));
    if (qId === 'imageAspectRatio') return ratios.map(r => ({ value: r.id, label: r.name }));
    if (qId === 'imageStyle') return styles.map(s => ({ value: s.id, label: s.name }));
    if (qId === 'textToneInImage') return tones.map(t => ({ value: t.id, label: t.name }));
    return [];
}

function resolveSelectLabel(
    qId: string,
    value: string,
    qOptions: { value: string; label?: string }[] | undefined,
    ratios: Ratio[],
    styles: Style[],
    tones: Tone[],
): string {
    if (qId === 'imageAspectRatio') return ratios.find(r => r.id === value)?.name || value || '系統預設';
    if (qId === 'imageStyle') return styles.find(s => s.id === value)?.name || value || '系統預設';
    if (qId === 'textToneInImage') return tones.find(t => t.id === value)?.name || value || '系統預設';
    if (qOptions && qOptions.length > 0) return qOptions.find(o => o.value === value)?.label || value || '系統預設';
    return value || '系統預設';
}

// ── ImageManagerModal ─────────────────────────────────────────────────────────

interface ImageManagerModalProps {
    title: string;
    fileIds: string[];
    editable: boolean;
    onClose: () => void;
    onUpdate: (fileIds: string[]) => void;
}

const ImageManagerModal: React.FC<ImageManagerModalProps> = ({ title, fileIds, editable, onClose, onUpdate }) => {
    const [localFileIds, setLocalFileIds] = useState<string[]>(fileIds);
    const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
    const [isUploading, setIsUploading] = useState(false);

    // Fetch signed URLs for existing fileIds
    useEffect(() => {
        const missing = localFileIds.filter(id => !previewUrls[id]);
        if (missing.length === 0) return;
        const API_BASE_URL = `${window.location.protocol}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}/api`;
        missing.forEach(id => {
            fetch(`${API_BASE_URL}/files/${id}`, { credentials: 'include' })
                .then(r => r.ok ? r.json() : null)
                .then(data => {
                    // 列表/縮圖場景優先用 thumbnailUrl，缺失時 fallback 用原圖 url
                    const previewSrc: string | undefined = data?.thumbnailUrl ?? data?.url;
                    if (previewSrc) {
                        setPreviewUrls(prev => ({ ...prev, [id]: previewSrc }));
                    }
                })
                .catch(() => {});
        });
    }, [localFileIds]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleAdd = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const added = Array.from(e.target.files || []);
        e.target.value = '';
        if (added.length === 0) return;
        setIsUploading(true);
        try {
            const results = await Promise.all(added.map(f => uploadImageFull(f)));
            const newUrls: Record<string, string> = {};
            results.forEach(r => { newUrls[r.fileId] = r.thumbnailUrl || r.url; });
            setPreviewUrls(prev => ({ ...prev, ...newUrls }));
            setLocalFileIds(prev => [...prev, ...results.map(r => r.fileId)]);
        } finally {
            setIsUploading(false);
        }
    };

    const handleRemove = (i: number) => {
        setLocalFileIds(prev => prev.filter((_, idx) => idx !== i));
    };

    const handleConfirm = () => {
        onUpdate(localFileIds);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md flex flex-col max-h-[85vh]">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
                    <h3 className="font-semibold text-slate-800 dark:text-slate-100 text-sm">{title}</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-5 py-4">
                    {localFileIds.length === 0 ? (
                        <p className="text-center text-sm text-slate-400 dark:text-slate-500 py-8">尚未上傳任何圖片</p>
                    ) : (
                        <div className="grid grid-cols-2 gap-3">
                            {localFileIds.map((id, i) => (
                                <div key={id} className="relative aspect-square rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 group bg-slate-100 dark:bg-slate-800">
                                    {previewUrls[id] ? (
                                        <img src={previewUrls[id]} className="w-full h-full object-cover" alt={`圖片 ${i + 1}`} />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <RunningLoader size="sm" label={null} />
                                        </div>
                                    )}
                                    {editable && (
                                        <button
                                            onClick={() => handleRemove(i)}
                                            className="absolute top-1.5 right-1.5 p-1 bg-red-500/80 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {editable && (
                        <label className={`mt-3 flex items-center justify-center gap-2 w-full py-3 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl text-sm transition-colors ${isUploading ? 'text-blue-400 border-blue-300 cursor-wait' : 'text-slate-400 dark:text-slate-500 hover:border-blue-400 hover:text-blue-500 cursor-pointer'}`}>
                            {isUploading ? (
                                <><div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" /> 上傳中...</>
                            ) : (
                                <><Plus size={16} /> 新增圖片</>
                            )}
                            <input type="file" multiple accept="image/*" onChange={handleAdd} className="hidden" disabled={isUploading} />
                        </label>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-200 dark:border-slate-700">
                    <button onClick={onClose} className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">
                        {editable ? '取消' : '關閉'}
                    </button>
                    {editable && (
                        <button
                            onClick={handleConfirm}
                            disabled={isUploading}
                            className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-xl hover:bg-blue-700 disabled:opacity-50 font-medium transition-colors"
                        >
                            確認
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// ── SettingsCard ──────────────────────────────────────────────────────────────

const SettingsCard: React.FC<SettingsCardProps> = ({
    isEditMode, editDraft, setEditDraft,
    flowModel, allFlowModels, styles, ratios, tones, toolkits,
    onSaveDraft, onCancelEdit, 
    compact,
}) => {
    const [imageModalQId, setImageModalQId] = useState<string | null>(null);

    // 工具包顯示名稱：優先從 allFlowModels 找（新架構），fallback 到舊 toolkits
    const currentToolkitName = editDraft.flowModelId
        ? (allFlowModels?.find(m => m.id === editDraft.flowModelId)?.title
            || toolkits.find(t => t.id === editDraft.flowModelId)?.title
            || editDraft.flowModelId)
        : flowModel.title || '不使用';

    const imageModalQuestion = imageModalQId
        ? flowModel.questions.find(q => q.id === imageModalQId)
        : null;

    return (
        <>
            {/* 套用工具包 */}
            <div className={rowClass(compact)}>
                <span className="text-slate-500">套用工具包</span>
                {isEditMode ? (
                    <select
                        value={editDraft.flowModelId || ''}
                        onChange={e => setEditDraft(d => ({ ...d, flowModelId: e.target.value || undefined }))}
                        className={selectClass}
                    >
                        <option value="">不使用</option>
                        {(allFlowModels ?? toolkits).map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
                    </select>
                ) : (
                    <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                        {currentToolkitName}
                    </span>
                )}
            </div>

            {/* Dynamic questions from FlowModel */}
            {flowModel.questions.map((q) => {
                const rawVal = editDraft.answers[q.id];

                if (q.type === 'select') {
                    const strVal = (rawVal as string) ?? '';
                    const options = resolveSelectOptions(q.id, q.options, ratios, styles, tones);
                    return (
                        <div key={q.id} className={rowClass(compact)}>
                            <span className="text-slate-500">{q.title}</span>
                            {isEditMode ? (
                                <select
                                    value={strVal}
                                    onChange={e => setEditDraft(d => ({ ...d, answers: { ...d.answers, [q.id]: e.target.value } }))}
                                    className={selectClass}
                                >
                                    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            ) : (
                                <span className="text-slate-700 dark:text-slate-200 font-medium">
                                    {resolveSelectLabel(q.id, strVal, q.options, ratios, styles, tones)}
                                </span>
                            )}
                        </div>
                    );
                }

                if (q.type === 'boolean') {
                    const boolVal = rawVal as boolean | undefined;
                    if (boolVal === undefined && !isEditMode) return null;
                    return (
                        <div key={q.id} className={rowClass(compact)}>
                            <span className="text-slate-500">{q.title}</span>
                            {isEditMode ? (
                                <button
                                    onClick={() => setEditDraft(d => ({ ...d, answers: { ...d.answers, [q.id]: !d.answers[q.id] } }))}
                                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${boolVal ? 'bg-blue-600 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'}`}
                                >
                                    {boolVal ? '是' : '否'}
                                </button>
                            ) : (
                                <span className={`font-medium ${boolVal ? 'text-blue-500 dark:text-blue-400' : 'text-slate-400'}`}>
                                    {boolVal ? '是' : '否'}
                                </span>
                            )}
                        </div>
                    );
                }

                if (q.type === 'text') {
                    const strVal = (rawVal as string) ?? '';
                    if (!strVal && !isEditMode) return null;
                    return (
                        <div key={q.id} className={`${compact ? 'pb-1.5 border-b border-slate-100 dark:border-slate-800' : 'pt-2'}`}>
                            <span className="text-slate-500 block mb-1.5">{q.title}</span>
                            {isEditMode ? (
                                <textarea
                                    value={strVal}
                                    onChange={e => setEditDraft(d => ({ ...d, answers: { ...d.answers, [q.id]: e.target.value } }))}
                                    rows={compact ? 3 : 4}
                                    className="w-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-2.5 py-1.5 text-xs text-slate-700 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-amber-500 resize-none"
                                />
                            ) : (
                                <div className={`p-2 bg-slate-100 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700/50 text-slate-600 dark:text-slate-300 text-xs leading-relaxed overflow-y-auto ${compact ? 'max-h-16' : 'max-h-32'}`}>
                                    {strVal}
                                </div>
                            )}
                        </div>
                    );
                }

                if (q.type === 'image') {
                    const fileIds = editDraft.imageFiles[q.id] ?? [];
                    const count = fileIds.length;
                    return (
                        <div key={q.id} className={rowClass(compact)}>
                            <span className="text-slate-500">{q.title}</span>
                            <button
                                onClick={() => setImageModalQId(q.id)}
                                className="flex items-center gap-1.5 text-xs text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                            >
                                <Images size={13} />
                                {count > 0 ? `${count} 張` : '未上傳'}
                            </button>
                        </div>
                    );
                }

                return null;
            })}

            {/* 新要求 (non-compact only) */}
            {isEditMode && !compact && (
                <div className="pt-2">
                    <span className="text-slate-500 block mb-2">新要求</span>
                    <textarea
                        value={editDraft.additionalRequirements}
                        onChange={e => setEditDraft(d => ({ ...d, additionalRequirements: e.target.value }))}
                        placeholder="例如：加入更多節慶元素、色調改為暖色系..."
                        rows={3}
                        className="w-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-700 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                    />
                </div>
            )}

            {/* Edit action buttons */}
            {isEditMode && (
                <div className={`flex gap-2 ${compact ? 'pt-1' : 'pt-4 border-t border-slate-100 dark:border-slate-800'}`}>
                    <button
                        onClick={() => onSaveDraft?.(editDraft)}
                        className={`flex-1 bg-amber-500 text-white rounded-xl font-bold hover:bg-amber-400 transition-all ${compact ? 'py-2 text-xs' : 'py-3 text-sm'}`}
                    >
                        儲存修改
                    </button>
                    <button
                        onClick={() => onCancelEdit?.()}
                        className={`flex-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-xl font-bold hover:bg-slate-200 dark:hover:bg-slate-700 transition-all ${compact ? 'py-2 text-xs' : 'py-3 text-sm'}`}
                    >
                        取消修改
                    </button>
                </div>
            )}

            {/* Image manager modal */}
            {imageModalQId && imageModalQuestion && (
                <ImageManagerModal
                    title={imageModalQuestion.title}
                    fileIds={editDraft.imageFiles[imageModalQId] ?? []}
                    editable={isEditMode}
                    onClose={() => setImageModalQId(null)}
                    onUpdate={fileIds => setEditDraft(d => ({
                        ...d,
                        imageFiles: { ...d.imageFiles, [imageModalQId]: fileIds },
                    }))}
                />
            )}
        </>
    );
};

export default SettingsCard;
